import type { MakeRequired } from '@beesoft/common';
import clsx from 'clsx';
import { debounce } from 'lodash';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { combineRefs } from '../../../common/functions/combine-refs';
import useTestTag from '../../../common/hooks/use-test-tag';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import EmptyFieldLabel from '../../labels/empty-field-label';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';

export interface InputProps extends MakeRequired<DynamicFieldComponentProps<string>, 'name'> {
  type?: string;
  onChangeEvent?: (event: ChangeEvent<HTMLInputElement>) => void;
  inputStyles?: string;
  debounceTime?: number;
  validations?: Array<InputValidation>;
  trimWhitespace?: boolean;
  min?: number;
  maxLength?: number;
  enableDebounce?: boolean;
}

export interface InputValidation {
  pattern: string;
  errorLabel: string;
}

export default function Input({
  name,
  label,
  id,
  type,
  value,
  placeholder,
  error,
  inputRef,
  readOnly,
  required,
  onChange,
  onChangeEvent,
  onFocus,
  inputStyles = 'py-1',
  disabled,
  debounceTime = 500,
  validations, // Validation order in the 'validations' prop determines the evaluation sequence, stopping at the first error.
  labelStyles,
  trimWhitespace = true,
  min,
  maxLength,
  enableDebounce = true,
}: InputProps) {
  const [stateValue, setStateValue] = useState<{
    value?: string | number;
    passedValue: boolean;
  }>();
  const [stateError, setStateError] = useState<string | undefined>();

  // Ref to track values that were already notified synchronously (when debounce is disabled).
  // This prevents the useEffect from calling onChange for values already propagated in the event handler.
  const syncNotifiedValue = useRef<string | undefined>(undefined);

  const tagCreator = useTestTag(label || name || '');
  const combinedRef = combineRefs(inputRef, tagCreator('input'));

  useEffect(() => {
    setStateError(error);
  }, [error]);

  useEffect(() => {
    // Reset the sync guard when value changes externally to avoid stale comparisons
    syncNotifiedValue.current = undefined;
    setStateValue({
      value,
      passedValue: true,
    });
  }, [value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger when state value changes (from user input), not when onChangeHandler updates
  useEffect(() => {
    if (stateValue?.passedValue === false) {
      const valueStr = String(stateValue?.value);
      // Guard against race conditions: skip if this value was already notified synchronously
      if (syncNotifiedValue.current !== valueStr) {
        onChangeHandler(valueStr);
      }
    }
  }, [stateValue?.value]);

  const debouncedOnChange = useMemo(
    () =>
      debounce((value: string) => {
        onChange?.(value);
      }, debounceTime),
    [onChange, debounceTime]
  );

  const onChangeHandler = useCallback(
    (value: string) => {
      if (enableDebounce) {
        debouncedOnChange(value);
      } else {
        onChange?.(value);
      }
    },
    [onChange, debouncedOnChange, enableDebounce]
  );

  const regEx = useMemo(() => {
    if (validations?.length) {
      return validations.map((validation) => {
        return {
          pattern: new RegExp(validation.pattern),
          message: validation.errorLabel,
        };
      });
    }
  }, [validations]);

  const onChangeEventHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const targetValue = event.target.value;
    let validationError;

    if (regEx) {
      for (const rule of regEx) {
        if (!rule.pattern.test(targetValue)) {
          validationError = rule.message;
          break;
        }
      }
      setStateError(validationError);
    }

    if (!validationError) {
      // When debounce is disabled, call onChange immediately (synchronously)
      // to avoid race conditions on form submit
      if (!enableDebounce) {
        // Update ref and call onChange synchronously BEFORE scheduling state update.
        // This ensures the ref guard is in place if React flushes state synchronously.
        syncNotifiedValue.current = targetValue;
        onChange?.(targetValue);
        setStateValue({
          value: targetValue,
          passedValue: true, // Mark as passed to skip the useEffect
        });
      } else {
        setStateValue({
          value: targetValue,
          passedValue: false,
        });
      }
      onChangeEvent?.(event);
    }
  };

  const onFocusHandler = () => {
    onFocus?.();
  };

  const onBlurHandler = (event: ChangeEvent<HTMLInputElement>) => {
    if (trimWhitespace && stateValue?.value) {
      const trimmedTargetValue = event.target.value.trim();
      if (trimmedTargetValue !== stateValue.value) {
        setStateValue({
          value: trimmedTargetValue,
          passedValue: false,
        });
      }
    }
    setStateError(error);
  };

  const inputFinalStyles = clsx(
    'appearance-none border relative w-full flex-1 placeholder-gray-3 text-sm focus:outline-hidden ',
    {
      'border-gray-4': !stateError,
      'border-error': stateError,
      'px-2 bg-mono-1': !readOnly,
      'border-none bg-transparent px-0': readOnly,
      hidden: readOnly && (!value || value.length === 0),
      'text-readonly': disabled,
    },
    inputStyles
  );

  const identifier = id ? String(id) : name;

  const wrapperStyles = clsx('flex w-full', {
    'flex-col': label && identifier,
    'flex-row-reverse flex-wrap': !label,
  });

  const childStyles = clsx('flex', { 'items-center': !label });

  return (
    <div className={wrapperStyles}>
      <div className={childStyles}>
        {label && identifier && <FieldLabel label={label} name={identifier} labelStyles={labelStyles} />}
        <RequiredIndicator readOnly={readOnly} required={required} />
      </div>
      <input
        ref={combinedRef}
        type={type}
        name={name}
        id={identifier}
        placeholder={placeholder}
        className={inputFinalStyles}
        onChange={onChangeEventHandler}
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
        value={stateValue?.value ?? ''}
        readOnly={readOnly}
        spellCheck={true}
        disabled={disabled}
        min={min}
        maxLength={maxLength}
        aria-required={!!required}
        aria-invalid={!!stateError}
        hidden={readOnly && (!value || value.length === 0)}
      />
      {readOnly && (!value || value.length === 0) && <EmptyFieldLabel />}
      {stateError && (
        <ErrorLabel className="w-full" role="alert">
          {stateError}
        </ErrorLabel>
      )}
    </div>
  );
}
